import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createMarketplaceDispute } from "../../api/myboothshareBilling";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "../marketplace/MarketplaceShell";

const DISPUTE_TYPES = [
  "no_access",
  "listing_mismatch",
  "cleanliness",
  "damage",
  "overstay",
  "safety",
  "billing_issue",
  "other",
];

export default function DisputeSubmissionPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    dispute_type: "listing_mismatch",
    summary: "",
    evidence_notes: "",
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await createMarketplaceDispute(
        Number(bookingId),
        {
          dispute_type: form.dispute_type,
          summary: form.summary,
          evidence: form.evidence_notes
            ? { notes: form.evidence_notes, uploads: [] }
            : { uploads: [] },
        },
        token
      );
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err.message || "Unable to open dispute."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketplaceShell>
      <section className="mx-auto max-w-4xl space-y-6">
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#2f3566] px-6 py-8 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
              Dispute Center
            </p>
            <h1 className="mt-3 text-3xl font-semibold">Open a rental dispute</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Use this form if the workspace access, condition, listing details, or billing
              outcome needs formal review before any release is processed.
            </p>
          </div>
          <div className="space-y-5 px-6 py-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Rental reference: #{bookingId}
            </div>
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {submitted ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                Your dispute was submitted and the release is now paused while the case is
                reviewed.
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Reason
                    </span>
                    <select
                      className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                      value={form.dispute_type}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, dispute_type: event.target.value }))
                      }
                    >
                      {DISPUTE_TYPES.map((value) => (
                        <option key={value} value={value}>
                          {value.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="space-y-2">
                  <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Summary
                  </span>
                  <textarea
                    className="min-h-[140px] w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900"
                    value={form.summary}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, summary: event.target.value }))
                    }
                    placeholder="Describe what happened, when it happened, and what outcome you believe is fair."
                    required
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Evidence notes
                  </span>
                  <textarea
                    className="min-h-[120px] w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900"
                    value={form.evidence_notes}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, evidence_notes: event.target.value }))
                    }
                    placeholder="Add any timestamps, conversations, photos, or support details you want finance to review."
                  />
                </label>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  Upload support can be added later if the existing file system is connected.
                  This v1 flow stores notes and evidence metadata so the case is traceable now.
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? "Submitting..." : "Submit dispute"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/account/rentals")}
                    className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
                  >
                    Back to rentals
                  </button>
                </div>
              </form>
            )}
            {submitted ? (
              <Link
                to="/account/rentals"
                className="inline-flex rounded-full bg-[#2f3566] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#252a53]"
              >
                Return to rentals
              </Link>
            ) : null}
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
